import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Tabs,
  Card,
  Stack,
  Title,
  Text,
  Button,
  Group,
  Badge,
  Alert,
  Loader,
  Center,
  Anchor,
  Accordion,
  SimpleGrid,
  Switch,
  TextInput,
  Table,
  Pagination
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconX } from '@tabler/icons-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

interface PendingUser {
  id: number;
  username: string;
  email: string;
  user_type: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  company_website?: string;
  phone_number?: string;
  dni?: string;
  cuit_cuil?: string;
  bio?: string;
  skills?: string[];
  career?: string;
  resume?: string | null;
  constancia_document?: string | null;
  cuit_cuil_document?: string | null;
  profile_picture?: string | null;
  created_at: string;
}

interface PendingJob {
  id: number;
  title: string;
  company_name: string;
  location?: string;
  job_type?: string;
  experience_range?: string;
  is_remote?: boolean;
  short_description?: string;
  description: string;
  requirements?: string;
  requires_at_title?: boolean;
  requires_at_diploma_uba?: boolean;
  requires_graduated?: boolean;
  requires_school_inclusion_course?: boolean;
  requires_hr_courses?: boolean;
  careers_affine?: string[];
  posted_by_username?: string;
  posted_by: number;
  created_at: string;
}

interface PhoneDuplicateUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  user_type: string;
  is_approved: boolean;
  rejected_at?: string | null;
  created_at: string;
  last_login?: string | null;
}

interface PhoneDuplicateGroup {
  phone_number: string;
  total_accounts: number;
  roles_involved: string[];
  pending_count: number;
  approved_count: number;
  validated: boolean;
  validated_at?: string | null;
  users: PhoneDuplicateUser[];
}

interface DniDuplicateUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  user_type: string;
  is_approved: boolean;
  rejected_at?: string | null;
  created_at: string;
  last_login?: string | null;
}

interface DniDuplicateGroup {
  dni: string;
  total_accounts: number;
  roles_involved: string[];
  pending_count: number;
  approved_count: number;
  validated: boolean;
  validated_at?: string | null;
  users: DniDuplicateUser[];
}

const ApprovalsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [pendingJobs, setPendingJobs] = useState<PendingJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approvingUserId, setApprovingUserId] = useState<number | null>(null);
  const [approvingJobId, setApprovingJobId] = useState<number | null>(null);
  const [phoneDuplicates, setPhoneDuplicates] = useState<PhoneDuplicateGroup[]>([]);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [validatingPhone, setValidatingPhone] = useState<string | null>(null);
  const [phoneFilters, setPhoneFilters] = useState({
    onlyPending: false,
    onlyJobSeekers: false,
    onlyResolved: false,
    startDate: '',
    endDate: '',
  });
  const [dniDuplicates, setDniDuplicates] = useState<DniDuplicateGroup[]>([]);
  const [dniLoading, setDniLoading] = useState(false);
  const [dniError, setDniError] = useState<string | null>(null);
  const [validatingDni, setValidatingDni] = useState<string | null>(null);
  const [dniFilters, setDniFilters] = useState({
    onlyPending: false,
    onlyJobSeekers: false,
    onlyResolved: false,
    startDate: '',
    endDate: '',
  });
  const [pendingUsersPage, setPendingUsersPage] = useState(1);
  const [pendingJobsPage, setPendingJobsPage] = useState(1);

  const pendingPageSize = 10;
  const pendingUsersTotalPages = Math.max(1, Math.ceil(pendingUsers.length / pendingPageSize));
  const pendingJobsTotalPages = Math.max(1, Math.ceil(pendingJobs.length / pendingPageSize));
  const visiblePendingUsers = pendingUsers.slice(
    (pendingUsersPage - 1) * pendingPageSize,
    pendingUsersPage * pendingPageSize
  );
  const visiblePendingJobs = pendingJobs.slice(
    (pendingJobsPage - 1) * pendingPageSize,
    pendingJobsPage * pendingPageSize
  );

  useEffect(() => {
    loadPendingItems();
  }, []);

  useEffect(() => {
    loadPhoneDuplicates();
  }, [phoneFilters]);

  useEffect(() => {
    loadDniDuplicates();
  }, [dniFilters]);

  useEffect(() => {
    setPendingUsersPage(1);
  }, [pendingUsers.length]);

  useEffect(() => {
    setPendingJobsPage(1);
  }, [pendingJobs.length]);

  const loadPendingItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const [usersResponse, jobsResponse] = await Promise.all([
        api.getPendingUsers(),
        api.getPendingJobs(),
      ]);
      // Handle paginated responses
      const usersData = Array.isArray(usersResponse) ? usersResponse : (usersResponse as any).results || [];
      const jobsData = Array.isArray(jobsResponse) ? jobsResponse : (jobsResponse as any).results || [];
      setPendingUsers(usersData);
      setPendingJobs(jobsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('approvals.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadPhoneDuplicates = async () => {
    try {
      setPhoneLoading(true);
      setPhoneError(null);
      const params: Record<string, string> = {};
      if (phoneFilters.onlyPending) params.only_pending = 'true';
      if (phoneFilters.onlyJobSeekers) params.only_job_seekers = 'true';
      if (phoneFilters.onlyResolved) params.only_resolved = 'true';
      if (phoneFilters.startDate) params.start_date = phoneFilters.startDate;
      if (phoneFilters.endDate) params.end_date = phoneFilters.endDate;

      const response = await api.getPhoneDuplicates(params);
      const data = Array.isArray(response) ? response : (response as any).results || [];
      setPhoneDuplicates(data);
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : t('approvals.phonesLoadError'));
    } finally {
      setPhoneLoading(false);
    }
  };

  const loadDniDuplicates = async () => {
    try {
      setDniLoading(true);
      setDniError(null);
      const params: Record<string, string> = {};
      if (dniFilters.onlyPending) params.only_pending = 'true';
      if (dniFilters.onlyJobSeekers) params.only_job_seekers = 'true';
      if (dniFilters.onlyResolved) params.only_resolved = 'true';
      if (dniFilters.startDate) params.start_date = dniFilters.startDate;
      if (dniFilters.endDate) params.end_date = dniFilters.endDate;

      const response = await api.getDniDuplicates(params);
      const data = Array.isArray(response) ? response : (response as any).results || [];
      setDniDuplicates(data);
    } catch (err) {
      setDniError(err instanceof Error ? err.message : t('approvals.dnisLoadError'));
    } finally {
      setDniLoading(false);
    }
  };

  const handleApproveUser = async (userId: number) => {
    try {
      setApprovingUserId(userId);
      await api.approveUser(userId);
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('approvals.approveUserError'));
    } finally {
      setApprovingUserId(null);
    }
  };

  const handleRejectUser = async (userId: number) => {
    try {
      setApprovingUserId(userId);
      await api.rejectUser(userId);
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('approvals.rejectUserError'));
    } finally {
      setApprovingUserId(null);
    }
  };

  const handleApproveJob = async (jobId: number) => {
    try {
      setApprovingJobId(jobId);
      await api.approveJob(jobId);
      setPendingJobs(pendingJobs.filter(j => j.id !== jobId));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('approvals.approveJobError'));
    } finally {
      setApprovingJobId(null);
    }
  };

  const handleRejectJob = async (jobId: number) => {
    try {
      setApprovingJobId(jobId);
      await api.rejectJob(jobId);
      setPendingJobs(pendingJobs.filter(j => j.id !== jobId));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('approvals.rejectJobError'));
    } finally {
      setApprovingJobId(null);
    }
  };

  const handleValidatePhone = async (phoneNumber: string) => {
    try {
      setValidatingPhone(phoneNumber);
      await api.validatePhoneDuplicate(phoneNumber);
      setPhoneDuplicates((prev) =>
        prev.map((group) =>
          group.phone_number === phoneNumber
            ? { ...group, validated: true, validated_at: new Date().toISOString() }
            : group
        )
      );
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : t('approvals.phoneValidateError'));
    } finally {
      setValidatingPhone(null);
    }
  };

  const handleValidateDni = async (dni: string) => {
    try {
      setValidatingDni(dni);
      await api.validateDniDuplicate(dni);
      setDniDuplicates((prev) =>
        prev.map((group) =>
          group.dni === dni
            ? { ...group, validated: true, validated_at: new Date().toISOString() }
            : group
        )
      );
    } catch (err) {
      setDniError(err instanceof Error ? err.message : t('approvals.dniValidateError'));
    } finally {
      setValidatingDni(null);
    }
  };

  const exportPhoneDuplicatesCsv = () => {
    const rows: string[] = [];
    rows.push([
      'phone_number',
      'total_accounts',
      'roles_involved',
      'pending_count',
      'approved_count',
      'validated',
      'user_name',
      'user_email',
      'user_role',
      'status',
      'created_at',
      'last_login',
    ].join(','));

    const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;

    phoneDuplicates.forEach((group) => {
      group.users.forEach((user) => {
        const status = user.is_approved
          ? t('approvals.statusApproved')
          : (user.rejected_at ? t('approvals.statusRejected') : t('approvals.statusPending'));
        rows.push([
          escape(group.phone_number),
          String(group.total_accounts),
          escape(group.roles_involved.map(formatUserType).join(' | ')),
          String(group.pending_count),
          String(group.approved_count),
          group.validated ? 'true' : 'false',
          escape(`${user.first_name || ''} ${user.last_name || ''}`.trim()),
          escape(user.email),
          escape(formatUserType(user.user_type)),
          escape(status),
          escape(new Date(user.created_at).toLocaleDateString()),
          escape(user.last_login ? new Date(user.last_login).toLocaleString() : '—'),
        ].join(','));
      });
    });

    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `phone-duplicates-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportDniDuplicatesCsv = () => {
    const rows: string[] = [];
    rows.push([
      'dni',
      'total_accounts',
      'roles_involved',
      'pending_count',
      'approved_count',
      'resolved',
      'user_name',
      'user_email',
      'user_role',
      'status',
      'created_at',
      'last_login',
    ].join(','));

    const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;

    dniDuplicates.forEach((group) => {
      group.users.forEach((user) => {
        const status = user.is_approved
          ? t('approvals.statusApproved')
          : (user.rejected_at ? t('approvals.statusRejected') : t('approvals.statusPending'));
        rows.push([
          escape(group.dni),
          String(group.total_accounts),
          escape(group.roles_involved.map(formatUserType).join(' | ')),
          String(group.pending_count),
          String(group.approved_count),
          group.validated ? 'true' : 'false',
          escape(`${user.first_name || ''} ${user.last_name || ''}`.trim()),
          escape(user.email),
          escape(formatUserType(user.user_type)),
          escape(status),
          escape(new Date(user.created_at).toLocaleDateString()),
          escape(user.last_login ? new Date(user.last_login).toLocaleString() : '—'),
        ].join(','));
      });
    });

    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `dni-duplicates-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatUserType = (userType: string) => {
    if (userType === 'JOB_SEEKER') return t('common.roles.jobSeeker');
    if (userType === 'JOB_POSTER') return t('common.roles.jobPoster');
    return userType;
  };

  const getUserStatus = (user: PhoneDuplicateUser | DniDuplicateUser) => {
    if (user.is_approved) return t('approvals.statusApproved');
    if (user.rejected_at) return t('approvals.statusRejected');
    return t('approvals.statusPending');
  };

  const careerLabelMap: Record<string, string> = {
    MUSICOTERAPIA: t('careers.musicotherapy'),
    PSICOLOGIA: t('careers.psychology'),
    PROFESORADO_PSICOLOGIA: t('careers.psychologyTeaching'),
    TERAPIA_OCUPACIONAL: t('careers.occupationalTherapy'),
  };

  const experienceRangeMap: Record<string, string> = {
    '0_1': t('experienceRanges.zeroToOne'),
    '2_3': t('experienceRanges.twoToThree'),
    '4_6': t('experienceRanges.fourToSix'),
    '7_PLUS': t('experienceRanges.sevenPlus'),
  };


  // Check if user has permission to view this page
  if (user && !['ADMIN', 'MODERATOR'].includes(user.user_type)) {
    return (
      <Card shadow="sm" padding="xl" radius="lg" withBorder>
        <Alert icon={<IconAlertCircle size={16} />} title="Access Denied" color="red" radius="md">
          {t('common.accessDenied')}
        </Alert>
      </Card>
    );
  }

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Card shadow="sm" padding="xl" radius="lg" withBorder>
      <Stack gap="lg">
        <Title order={2}>{t('approvals.title')}</Title>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" radius="md">
            {error}
          </Alert>
        )}

        <Tabs defaultValue="users" radius="md">
          <Tabs.List>
            <Tabs.Tab value="users" leftSection={<Badge>{pendingUsers.length}</Badge>}>
              {t('approvals.pendingUsers')}
            </Tabs.Tab>
            <Tabs.Tab value="jobs" leftSection={<Badge>{pendingJobs.length}</Badge>}>
              {t('approvals.pendingJobs')}
            </Tabs.Tab>
            <Tabs.Tab value="phones" leftSection={<Badge>{phoneDuplicates.length}</Badge>}>
              {t('approvals.phoneDuplicates')}
            </Tabs.Tab>
            <Tabs.Tab value="dnis" leftSection={<Badge>{dniDuplicates.length}</Badge>}>
              {t('approvals.dniDuplicates')}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="users" pt="xl">
            {pendingUsers.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                {t('approvals.noPendingUsers')}
              </Text>
            ) : (
              <Stack gap="md">
                {visiblePendingUsers.map(u => (
                  <Card key={u.id} withBorder p="md" radius="md">
                    <Stack gap="sm">
                      <Group justify="space-between" wrap="wrap" align="flex-start">
                        <div>
                          <Text fw={600}>{u.username}</Text>
                          <Text size="sm" c="dimmed">{u.email}</Text>
                          {u.first_name && u.last_name && (
                            <Text size="sm">{u.first_name} {u.last_name}</Text>
                          )}
                          {u.company_name && (
                            <Text size="sm">{u.company_name}</Text>
                          )}
                          {u.user_type === 'JOB_SEEKER' && u.constancia_document && (
                            <Text size="sm">
                              {t('approvals.constancia')}: {' '}
                              <Anchor href={u.constancia_document} target="_blank" rel="noopener noreferrer">
                                {t('approvals.viewDocument')}
                              </Anchor>
                            </Text>
                          )}
                        </div>
                        <Badge>{formatUserType(u.user_type)}</Badge>
                      </Group>
                      <Text size="xs" c="dimmed">
                        {t('approvals.requested')}: {new Date(u.created_at).toLocaleDateString()}
                      </Text>
                      <Stack gap="xs">
                        {u.phone_number && (
                          <Text size="sm">
                            {t('approvals.phone')}: {u.phone_number}
                          </Text>
                        )}
                        {u.dni && (
                          <Text size="sm">
                            {t('approvals.dni')}: {u.dni}
                          </Text>
                        )}
                        {u.cuit_cuil && (
                          <Text size="sm">
                            {t('approvals.cuitCuilNumber')}: {u.cuit_cuil}
                          </Text>
                        )}
                        {u.career && (
                          <Text size="sm">
                            {t('approvals.career')}: {u.career}
                          </Text>
                        )}
                        {u.skills && u.skills.length > 0 && (
                          <Text size="sm">
                            {t('approvals.skills')}: {u.skills.join(', ')}
                          </Text>
                        )}
                        {u.bio && (
                          <Text size="sm">
                            {t('approvals.bio')}: {u.bio}
                          </Text>
                        )}
                        {u.company_website && (
                          <Text size="sm">
                            {t('approvals.companyWebsite')}: {u.company_website}
                          </Text>
                        )}
                        {u.resume && (
                          <Text size="sm">
                            {t('approvals.resume')}: {' '}
                            <Anchor href={u.resume} target="_blank" rel="noopener noreferrer">
                              {t('approvals.viewDocument')}
                            </Anchor>
                          </Text>
                        )}
                        {u.user_type === 'JOB_POSTER' && u.cuit_cuil_document && (
                          <Text size="sm">
                            {t('approvals.cuitCuil')}: {' '}
                            <Anchor href={u.cuit_cuil_document} target="_blank" rel="noopener noreferrer">
                              {t('approvals.viewDocument')}
                            </Anchor>
                          </Text>
                        )}
                        {u.profile_picture && (
                          <Text size="sm">
                            {t('approvals.profilePicture')}: {' '}
                            <Anchor href={u.profile_picture} target="_blank" rel="noopener noreferrer">
                              {t('approvals.viewDocument')}
                            </Anchor>
                          </Text>
                        )}
                      </Stack>
                      <Group wrap="wrap">
                        <Button
                          size="sm"
                          color="green"
                          leftSection={<IconCheck size={16} />}
                          loading={approvingUserId === u.id}
                          onClick={() => handleApproveUser(u.id)}
                        >
                          {t('approvals.approve')}
                        </Button>
                        <Button
                          size="sm"
                          color="red"
                          variant="outline"
                          leftSection={<IconX size={16} />}
                          loading={approvingUserId === u.id}
                          onClick={() => handleRejectUser(u.id)}
                        >
                          {t('approvals.reject')}
                        </Button>
                      </Group>
                    </Stack>
                  </Card>
                ))}
                {pendingUsers.length > pendingPageSize && (
                  <Group justify="center" mt="xs">
                    <Pagination
                      value={pendingUsersPage}
                      onChange={setPendingUsersPage}
                      total={pendingUsersTotalPages}
                    />
                  </Group>
                )}
              </Stack>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="jobs" pt="xl">
            {pendingJobs.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                {t('approvals.noPendingJobs')}
              </Text>
            ) : (
              <Stack gap="md">
                {visiblePendingJobs.map(j => (
                  <Card key={j.id} withBorder p="md" radius="md">
                    <Stack gap="sm">
                      <Group justify="space-between" wrap="wrap" align="flex-start">
                        <div>
                          {j.careers_affine && j.careers_affine.length > 0 && (
                            <Group gap="xs" wrap="wrap" mb={6}>
                              {j.careers_affine.map((career) => (
                                <Badge key={career} variant="outline" color="orange" radius="xl">
                                  {careerLabelMap[career] || career}
                                </Badge>
                              ))}
                            </Group>
                          )}
                          <Text fw={600}>{j.title}</Text>
                          <Text size="sm" c="dimmed">
                            {j.company_name}
                            {j.location ? ` • ${j.location}` : ''}
                            {j.is_remote ? ` • ${t('common.remote')}` : ''}
                          </Text>
                          {(j.job_type || j.experience_range) && (
                            <Group gap="xs" wrap="wrap" mt="xs">
                              {j.job_type && (
                                <Badge color="orange" variant="light">
                                  {j.job_type.replace('_', ' ')}
                                </Badge>
                              )}
                              {j.experience_range && (
                                <Badge color="orange" variant="light">
                                  {experienceRangeMap[j.experience_range] || j.experience_range}
                                </Badge>
                              )}
                            </Group>
                          )}
                          {(j.requires_at_title
                            || j.requires_at_diploma_uba
                            || j.requires_graduated
                            || j.requires_school_inclusion_course
                            || j.requires_hr_courses
                            || j.requirements) && (
                              <Stack gap={4} mt="xs">
                                <Group gap="xs" wrap="wrap">
                                  {j.requires_at_title && (
                                    <Badge color="orange" variant="light">{t('jobDetail.requiresAtCourse')}</Badge>
                                  )}
                                  {j.requires_at_diploma_uba && (
                                    <Badge color="orange" variant="light">{t('jobDetail.requiresAtDiplomaUba')}</Badge>
                                  )}
                                  {j.requires_graduated && (
                                    <Badge color="orange" variant="light">{t('jobDetail.requiresDegreeTitle')}</Badge>
                                  )}
                                  {j.requires_school_inclusion_course && (
                                    <Badge color="orange" variant="light">{t('jobDetail.requiresSchoolInclusionCourse')}</Badge>
                                  )}
                                  {j.requires_hr_courses && (
                                    <Badge color="orange" variant="light">{t('jobDetail.requiresHrCourses')}</Badge>
                                  )}
                                </Group>
                                {j.requirements && (
                                  <Text size="sm">
                                    {t('jobDetail.otherSkills')} {j.requirements}
                                  </Text>
                                )}
                              </Stack>
                            )}
                          {j.short_description && (
                            <Text size="sm" mt="xs">
                              {j.short_description}
                            </Text>
                          )}
                          <Text size="sm" mt="xs">
                            {j.description}
                          </Text>
                        </div>
                      </Group>
                      <Text size="xs" c="dimmed">
                        {t('approvals.posted')}: {new Date(j.created_at).toLocaleDateString()}
                        {j.posted_by_username ? ` • ${j.posted_by_username}` : ''}
                      </Text>
                      <Group wrap="wrap">
                        <Button
                          size="sm"
                          variant="light"
                          component={Link}
                          to={`/jobs/${j.id}/edit`}
                        >
                          {t('approvals.editJob')}
                        </Button>
                        <Button
                          size="sm"
                          color="green"
                          leftSection={<IconCheck size={16} />}
                          loading={approvingJobId === j.id}
                          onClick={() => handleApproveJob(j.id)}
                        >
                          {t('approvals.approve')}
                        </Button>
                        <Button
                          size="sm"
                          color="red"
                          variant="outline"
                          leftSection={<IconX size={16} />}
                          loading={approvingJobId === j.id}
                          onClick={() => handleRejectJob(j.id)}
                        >
                          {t('approvals.reject')}
                        </Button>
                      </Group>
                    </Stack>
                  </Card>
                ))}
                {pendingJobs.length > pendingPageSize && (
                  <Group justify="center" mt="xs">
                    <Pagination
                      value={pendingJobsPage}
                      onChange={setPendingJobsPage}
                      total={pendingJobsTotalPages}
                    />
                  </Group>
                )}
              </Stack>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="phones" pt="xl">
            <Stack gap="md">
              <Group justify="space-between" wrap="wrap">
                <Title order={4}>{t('approvals.phoneDuplicatesTitle')}</Title>
                <Button size="sm" variant="light" onClick={exportPhoneDuplicatesCsv}>
                  {t('approvals.exportCsv')}
                </Button>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
                <Switch
                  label={t('approvals.onlyPending')}
                  checked={phoneFilters.onlyPending}
                  onChange={(event) => {
                    const checked = event.currentTarget.checked;
                    setPhoneFilters((prev) => ({ ...prev, onlyPending: checked }));
                  }}
                />
                <Switch
                  label={t('approvals.onlyJobSeekers')}
                  checked={phoneFilters.onlyJobSeekers}
                  onChange={(event) => {
                    const checked = event.currentTarget.checked;
                    setPhoneFilters((prev) => ({ ...prev, onlyJobSeekers: checked }));
                  }}
                />
                <Switch
                  label={t('approvals.onlyResolved')}
                  checked={phoneFilters.onlyResolved}
                  onChange={(event) => {
                    const checked = event.currentTarget.checked;
                    setPhoneFilters((prev) => ({ ...prev, onlyResolved: checked }));
                  }}
                />
                <TextInput
                  type="date"
                  label={t('approvals.startDate')}
                  value={phoneFilters.startDate}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setPhoneFilters((prev) => ({ ...prev, startDate: value }));
                  }}
                />
                <TextInput
                  type="date"
                  label={t('approvals.endDate')}
                  value={phoneFilters.endDate}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setPhoneFilters((prev) => ({ ...prev, endDate: value }));
                  }}
                />
              </SimpleGrid>
              {phoneError && (
                <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" radius="md">
                  {phoneError}
                </Alert>
              )}
              {phoneLoading ? (
                <Center py="xl">
                  <Loader />
                </Center>
              ) : phoneDuplicates.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {t('approvals.noPhoneDuplicates')}
                </Text>
              ) : (
                <Accordion variant="separated" radius="md">
                  {phoneDuplicates.map((group) => (
                    <Accordion.Item key={group.phone_number} value={group.phone_number}>
                      <Accordion.Control>
                        <Group justify="space-between" wrap="wrap">
                          <div>
                            <Text fw={600}>{group.phone_number}</Text>
                            <Text size="sm" c="dimmed">
                              {t('approvals.rolesInvolved')}: {group.roles_involved.map(formatUserType).join(', ')}
                            </Text>
                          </div>
                          <Group gap="xs" wrap="wrap">
                            <Badge variant="light">{t('approvals.totalAccounts', { count: group.total_accounts })}</Badge>
                            <Badge color="orange" variant="light">{t('approvals.pendingAccounts', { count: group.pending_count })}</Badge>
                            <Badge color="green" variant="light">{t('approvals.approvedAccounts', { count: group.approved_count })}</Badge>
                            {group.validated && <Badge color="green" variant="outline">{t('approvals.validated')}</Badge>}
                          </Group>
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap="sm">
                          <Group justify="space-between" wrap="wrap">
                            <Text size="sm" c="dimmed">
                              {group.validated_at
                                ? t('approvals.validatedAt', { date: new Date(group.validated_at).toLocaleString() })
                                : t('approvals.notValidated')}
                            </Text>
                            <Button
                              size="xs"
                              variant="light"
                              onClick={() => handleValidatePhone(group.phone_number)}
                              loading={validatingPhone === group.phone_number}
                              disabled={group.validated}
                            >
                              {t('approvals.markValidated')}
                            </Button>
                          </Group>
                          <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th>{t('approvals.userName')}</Table.Th>
                                <Table.Th>{t('approvals.userEmail')}</Table.Th>
                                <Table.Th>{t('approvals.userRole')}</Table.Th>
                                <Table.Th>{t('approvals.userStatus')}</Table.Th>
                                <Table.Th>{t('approvals.userCreated')}</Table.Th>
                                <Table.Th>{t('approvals.userLastLogin')}</Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {group.users.map((userItem) => (
                                <Table.Tr key={userItem.id}>
                                  <Table.Td>{`${userItem.first_name || ''} ${userItem.last_name || ''}`.trim() || '—'}</Table.Td>
                                  <Table.Td>{userItem.email}</Table.Td>
                                  <Table.Td>{formatUserType(userItem.user_type)}</Table.Td>
                                  <Table.Td>{getUserStatus(userItem)}</Table.Td>
                                  <Table.Td>{new Date(userItem.created_at).toLocaleDateString()}</Table.Td>
                                  <Table.Td>{userItem.last_login ? new Date(userItem.last_login).toLocaleString() : '—'}</Table.Td>
                                </Table.Tr>
                              ))}
                            </Table.Tbody>
                          </Table>
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="dnis" pt="xl">
            <Stack gap="md">
              <Group justify="space-between" wrap="wrap">
                <Title order={4}>{t('approvals.dniDuplicatesTitle')}</Title>
                <Button size="sm" variant="light" onClick={exportDniDuplicatesCsv}>
                  {t('approvals.exportCsv')}
                </Button>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
                <Switch
                  label={t('approvals.onlyPending')}
                  checked={dniFilters.onlyPending}
                  onChange={(event) => {
                    const checked = event.currentTarget.checked;
                    setDniFilters((prev) => ({ ...prev, onlyPending: checked }));
                  }}
                />
                <Switch
                  label={t('approvals.onlyJobSeekers')}
                  checked={dniFilters.onlyJobSeekers}
                  onChange={(event) => {
                    const checked = event.currentTarget.checked;
                    setDniFilters((prev) => ({ ...prev, onlyJobSeekers: checked }));
                  }}
                />
                <Switch
                  label={t('approvals.onlyResolved')}
                  checked={dniFilters.onlyResolved}
                  onChange={(event) => {
                    const checked = event.currentTarget.checked;
                    setDniFilters((prev) => ({ ...prev, onlyResolved: checked }));
                  }}
                />
                <TextInput
                  type="date"
                  label={t('approvals.startDate')}
                  value={dniFilters.startDate}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setDniFilters((prev) => ({ ...prev, startDate: value }));
                  }}
                />
                <TextInput
                  type="date"
                  label={t('approvals.endDate')}
                  value={dniFilters.endDate}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setDniFilters((prev) => ({ ...prev, endDate: value }));
                  }}
                />
              </SimpleGrid>
              {dniError && (
                <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" radius="md">
                  {dniError}
                </Alert>
              )}
              {dniLoading ? (
                <Center py="xl">
                  <Loader />
                </Center>
              ) : dniDuplicates.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {t('approvals.noDniDuplicates')}
                </Text>
              ) : (
                <Accordion variant="separated" radius="md">
                  {dniDuplicates.map((group) => (
                    <Accordion.Item key={group.dni} value={group.dni}>
                      <Accordion.Control>
                        <Group justify="space-between" wrap="wrap">
                          <div>
                            <Text fw={600}>{group.dni}</Text>
                            <Text size="sm" c="dimmed">
                              {t('approvals.rolesInvolved')}: {group.roles_involved.map(formatUserType).join(', ')}
                            </Text>
                          </div>
                          <Group gap="xs" wrap="wrap">
                            <Badge variant="light">{t('approvals.totalAccounts', { count: group.total_accounts })}</Badge>
                            <Badge color="orange" variant="light">{t('approvals.pendingAccounts', { count: group.pending_count })}</Badge>
                            <Badge color="green" variant="light">{t('approvals.approvedAccounts', { count: group.approved_count })}</Badge>
                            {group.validated && <Badge color="green" variant="outline">{t('approvals.validated')}</Badge>}
                          </Group>
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap="sm">
                          <Group justify="space-between" wrap="wrap">
                            <Text size="sm" c="dimmed">
                              {group.validated_at
                                ? t('approvals.validatedAt', { date: new Date(group.validated_at).toLocaleString() })
                                : t('approvals.notValidated')}
                            </Text>
                            <Button
                              size="xs"
                              variant="light"
                              onClick={() => handleValidateDni(group.dni)}
                              loading={validatingDni === group.dni}
                              disabled={group.validated}
                            >
                              {t('approvals.markValidated')}
                            </Button>
                          </Group>
                          <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th>{t('approvals.userName')}</Table.Th>
                                <Table.Th>{t('approvals.userEmail')}</Table.Th>
                                <Table.Th>{t('approvals.userRole')}</Table.Th>
                                <Table.Th>{t('approvals.userStatus')}</Table.Th>
                                <Table.Th>{t('approvals.userCreated')}</Table.Th>
                                <Table.Th>{t('approvals.userLastLogin')}</Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {group.users.map((userItem) => (
                                <Table.Tr key={userItem.id}>
                                  <Table.Td>{`${userItem.first_name || ''} ${userItem.last_name || ''}`.trim() || '—'}</Table.Td>
                                  <Table.Td>{userItem.email}</Table.Td>
                                  <Table.Td>{formatUserType(userItem.user_type)}</Table.Td>
                                  <Table.Td>{getUserStatus(userItem)}</Table.Td>
                                  <Table.Td>{new Date(userItem.created_at).toLocaleDateString()}</Table.Td>
                                  <Table.Td>{userItem.last_login ? new Date(userItem.last_login).toLocaleString() : '—'}</Table.Td>
                                </Table.Tr>
                              ))}
                            </Table.Tbody>
                          </Table>
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Card>
  );
};

export default ApprovalsPage;
